import * as React from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { getClientById, getKwestionariushByClientId, downloadSignedKwestionariush, getUser } from "../service/weexpertService";
import { useQuery } from "react-query";
import { Loader } from "@/components/ProtectedRoute";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { format } from "date-fns";
import { CheckIcon, XIcon, EyeIcon, EditIcon, DownloadIcon } from "lucide-react";
import { toast } from '@/components/ui/use-toast';
import { useState } from "react";
import fileDownload from "js-file-download";

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    return format(new Date(dateString), 'dd/MM/yyyy HH:mm');
  } catch {
    return dateString;
  }
};

const getStatusBadge = (status) => {
  const statusConfig = {
    DRAFT: { label: 'Szkic', color: 'bg-yellow-500' },
    SUBMITTED: { label: 'Przesłany', color: 'bg-blue-500' },
    SEND_TO_SIGNING: { label: 'Wysłany do podpisu', color: 'bg-orange-500' },
    SIGNED: { label: 'Podpisany', color: 'bg-purple-500' },
    APPROVED: { label: 'Zatwierdzony', color: 'bg-green-500' },
    REJECTED: { label: 'Odrzucony', color: 'bg-red-500' }
  };

  const config = statusConfig[status] || { label: status, color: 'bg-gray-500' };
  
  return (
    <Badge className={`${config.color} text-white`}>
      {config.label}
    </Badge>
  );
};

export const ClientKwestionariushPage = () => {
  const { t } = useTranslation();
  const { id: clientId } = useParams();
  const [downloadLoading, setDownloadLoading] = useState(false);

  const { data: client, isLoading: clientLoading } = useQuery({
    queryKey: ["client", clientId],
    queryFn: () => getClientById(clientId),
  });

  const { data: kwestionariush, isLoading: kwestionariushLoading, error } = useQuery({
    queryKey: ["kwestionariush", clientId],
    queryFn: () => getKwestionariushByClientId(clientId),
    retry: false
  });

  // Get current user to check permissions
  const { data: userData } = useQuery({
    queryKey: ['userMe'],
    queryFn: getUser,
    retry: false,
    refetchOnWindowFocus: false
  });

  const handleDownloadSignedDocument = async () => {
    if (!kwestionariush?.data?.id) {
      toast({
        title: 'Błąd',
        description: 'Nie można pobrać podpisanego dokumentu',
        variant: "destructive"
      });
      return;
    }

    setDownloadLoading(true);
    try {
      const response = await downloadSignedKwestionariush(kwestionariush.data.id);
      fileDownload(response.data, `signed-kwestionariusz-${kwestionariush.data.id}.pdf`);
      toast({
        title: 'Sukces',
        description: 'Podpisany dokument został pobrany',
      });
    } catch (error) {
      console.error('Error downloading signed document:', error);
      toast({
        title: 'Błąd',
        description: error.response?.data?.message || 'Wystąpił błąd podczas pobierania podpisanego dokumentu',
        variant: "destructive"
      });
    } finally {
      setDownloadLoading(false);
    }
  };

  // Check if user is assistant or admin
  const canDownload = userData?.data?.roles && ['ADMIN', 'HEAD_OF_DEPARTMENT', 'SUPER_ADMIN', 'ASSISTANT'].includes(userData.data.roles);

  if (clientLoading) {
    return <Loader />;
  }

  if (!client?.data) {
    return (
      <div className="relative bg-grayLightMainBg px-8">
        <div className="py-8">
          <div className="text-2xl text-red-600">Klient nie został znaleziony</div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative bg-grayLightMainBg px-8">
      {/* Header */}
      <div className="sticky left-0 top-16 z-10 w-full bg-grayLightMainBg">
        <div className="flex justify-between py-8 items-center">
          <div className="w-5/12 text-4xl">Kwestionariusz</div>
          <div className="flex justify-end">
            <Link to={`/admin/client/details/${clientId}`}>
              <Button className="min-w-20">{t("button_back")}</Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Client Info */}
      <div className="w-full pt-2 pb-6">
        <div className="text-2xl">
          {client.data.firstName} {client.data.lastName}
        </div>
        <div className="text-gray-600">
          {client.data.email}
        </div>
      </div>

      {/* Kwestionariush Content */}
      {kwestionariushLoading ? (
        <div className="flex justify-center py-8">
          <Loader />
        </div>
      ) : error?.response?.status === 404 || !kwestionariush?.data ? (
        /* No Kwestionariush Found */
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Status kwestionariusza</span>
              <Badge variant="secondary" className="bg-gray-500 text-white">
                <XIcon className="w-3 h-3 mr-1" />
                Brak
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8">
              <p className="text-gray-500 mb-4">
                Klient nie wypełnił jeszcze kwestionariusza
              </p>
              <p className="text-sm text-gray-400">
                Kwestionariusz będzie widoczny po jego wypełnieniu przez klienta
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        /* Kwestionariush Exists */
        <div className="space-y-6">
          {/* Kwestionariush Summary Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Kwestionariusz klienta</span>
                {getStatusBadge(kwestionariush.data.status)}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                <div>
                  <span className="font-medium text-gray-700">Utworzony:</span>
                  <p className="text-gray-900">{formatDate(kwestionariush.data.createdAt)}</p>
                </div>
                {kwestionariush.data.submittedAt && (
                  <div>
                    <span className="font-medium text-gray-700">Przesłany:</span>
                    <p className="text-gray-900">{formatDate(kwestionariush.data.submittedAt)}</p>
                  </div>
                )}
                <div>
                  <span className="font-medium text-gray-700">Ostatnia aktualizacja:</span>
                  <p className="text-gray-900">{formatDate(kwestionariush.data.updatedAt)}</p>
                </div>
              </div>

              {/* Quick Info Summary */}
              <div className="border-t pt-4">
                <h4 className="font-medium text-gray-900 mb-3">Podsumowanie oświadczeń:</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div className="flex items-center">
                    {kwestionariush.data.data?.declarations?.employment?.isEmployed ? (
                      <CheckIcon className="w-4 h-4 text-green-500 mr-1" />
                    ) : (
                      <XIcon className="w-4 h-4 text-gray-400 mr-1" />
                    )}
                    <span className={kwestionariush.data.data?.declarations?.employment?.isEmployed ? "text-green-700" : "text-gray-500"}>
                      Zatrudniony
                    </span>
                  </div>
                  
                  <div className="flex items-center">
                    {kwestionariush.data.data?.declarations?.studentStatus ? (
                      <CheckIcon className="w-4 h-4 text-green-500 mr-1" />
                    ) : (
                      <XIcon className="w-4 h-4 text-gray-400 mr-1" />
                    )}
                    <span className={kwestionariush.data.data?.declarations?.studentStatus ? "text-green-700" : "text-gray-500"}>
                      Student
                    </span>
                  </div>

                  <div className="flex items-center">
                    {kwestionariush.data.data?.declarations?.pension?.isPensioner ? (
                      <CheckIcon className="w-4 h-4 text-green-500 mr-1" />
                    ) : (
                      <XIcon className="w-4 h-4 text-gray-400 mr-1" />
                    )}
                    <span className={kwestionariush.data.data?.declarations?.pension?.isPensioner ? "text-green-700" : "text-gray-500"}>
                      Emeryt/Rencista
                    </span>
                  </div>

                  <div className="flex items-center">
                    {kwestionariush.data.data?.declarations?.disability?.hasDisability ? (
                      <CheckIcon className="w-4 h-4 text-green-500 mr-1" />
                    ) : (
                      <XIcon className="w-4 h-4 text-gray-400 mr-1" />
                    )}
                    <span className={kwestionariush.data.data?.declarations?.disability?.hasDisability ? "text-green-700" : "text-gray-500"}>
                      Niepełnosprawność
                    </span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-3 mt-6 pt-4 border-t">
                <Link to={`/client/${clientId}/kwestionariush/${kwestionariush.data.id}`}>
                  <Button variant="outline" className="flex items-center">
                    <EyeIcon className="w-4 h-4 mr-2" />
                    Wyświetl szczegóły
                  </Button>
                </Link>
                
                {/* Download signed document button - only for assistants/admins when status is SIGNED */}
                {canDownload && kwestionariush.data.status === 'SIGNED' && (
                  <Button 
                    variant="outline" 
                    onClick={handleDownloadSignedDocument}
                    disabled={downloadLoading}
                    className="flex items-center"
                  >
                    <DownloadIcon className="w-4 h-4 mr-2" />
                    {downloadLoading ? 'Pobieranie podpisanego dokumentu...' : 'Pobierz podpisany dokument'}
                  </Button>
                )}
                
                {/* Only show edit for DRAFT status or if user has permissions */}
                {kwestionariush.data.status === 'DRAFT' && (
                  <Button className="flex items-center">
                    <EditIcon className="w-4 h-4 mr-2" />
                    Edytuj
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Personal Information Summary */}
          <Card>
            <CardHeader>
              <CardTitle>Informacje podstawowe</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-700">Miejsce urodzenia:</span>
                  <p className="text-gray-900">{kwestionariush.data.data?.personalInfo?.birthPlace || 'N/A'}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Urząd Skarbowy:</span>
                  <p className="text-gray-900">{kwestionariush.data.data?.personalInfo?.taxOffice || 'N/A'}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Oddział NFZ:</span>
                  <p className="text-gray-900">{kwestionariush.data.data?.personalInfo?.nfzBranch || 'N/A'}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Zgoda na przetwarzanie danych:</span>
                  <p className={`font-medium ${kwestionariush.data.data?.declarations?.dataProcessingConsent ? 'text-green-600' : 'text-red-600'}`}>
                    {kwestionariush.data.data?.declarations?.dataProcessingConsent ? 'Tak' : 'Nie'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};